import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import '../styles/shareFileModal.scss';
import ButtonComponent from './ButtonComponent';
import Popup from './Popup';
import { t } from 'i18next';
import globalStore from '../store';
import customEventHandler from '../helper/customEvents';
import { getImageSrc } from '../helper/imageBlobCache';
var ShareFileModal = function (_a) {
    var _b, _c;
    var onClose = _a.onClose;
    var displayMode = globalStore.useStoreState(function (state) { return state.playerStore; }).displayMode;
    var beakHeight = 18;
    var panelRef = useRef(null);
    var triggerRef = useRef(null);
    var _d = useState(null), imageFeedback = _d[0], setImageFeedback = _d[1];
    // Trigger a copy/download image and surface the outcome (plus the saved /
    // copied image's size + dimensions) in a confirmation popup. WhiteBoard does
    // the capture and dispatches `screenshotCopied` when done. Showing the size
    // makes the iPad "0-byte / blank capture" failure visible instead of silent.
    var triggerImageAction = function (id) {
        var handler = function (data) {
            customEventHandler.removeEventListener('screenshotCopied', handler);
            setImageFeedback({ ok: !!(data === null || data === void 0 ? void 0 : data.ok), reason: data === null || data === void 0 ? void 0 : data.reason, info: data === null || data === void 0 ? void 0 : data.info });
        };
        customEventHandler.addEventListener('screenshotCopied', handler);
        customEventHandler.dispatchEvent('headerClicked', { id: id });
    };
    // Auto-focus first button on open + capture trigger for restore
    useEffect(function () {
        triggerRef.current = document.activeElement;
        requestAnimationFrame(function () {
            var _a;
            var firstFocusable = (_a = panelRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]), input:not([disabled])');
            firstFocusable === null || firstFocusable === void 0 ? void 0 : firstFocusable.focus();
        });
        return function () {
            if (triggerRef.current && triggerRef.current.focus) {
                triggerRef.current.focus();
            }
        };
    }, []);
    // Escape to close + focus trap (same pattern as SaveBtnWithPopup)
    useEffect(function () {
        var handleKeyDown = function (e) {
            var _a;
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopImmediatePropagation();
                onClose === null || onClose === void 0 ? void 0 : onClose();
                return;
            }
            // Arrow key navigation within the dialog (same as InputWithBtn)
            if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
                e.stopImmediatePropagation();
                e.preventDefault();
                if (!panelRef.current)
                    return;
                var focusables = Array.from(panelRef.current.querySelectorAll('button:not([disabled]):not([aria-hidden="true"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (el) { return el.offsetParent !== null; });
                if (focusables.length === 0)
                    return;
                var currentIndex = focusables.indexOf(document.activeElement);
                var nextIndex = void 0;
                if (e.key === 'Home') {
                    nextIndex = 0;
                }
                else if (e.key === 'End') {
                    nextIndex = focusables.length - 1;
                }
                else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                    nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % focusables.length;
                }
                else {
                    nextIndex = currentIndex === -1 ? 0 : (currentIndex - 1 + focusables.length) % focusables.length;
                }
                (_a = focusables[nextIndex]) === null || _a === void 0 ? void 0 : _a.focus();
                return;
            }
            if (e.key === 'Tab' && panelRef.current) {
                // If focus is in a portaled context menu, let it handle Tab itself
                var contextMenu = document.querySelector('.iwb-contextMenu');
                if (contextMenu && contextMenu.contains(document.activeElement))
                    return;
                var focusables = Array.from(panelRef.current.querySelectorAll('button:not([disabled]):not([aria-hidden="true"]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter(function (el) {
                    if (el.offsetParent === null)
                        return false;
                    if (el.matches('input[type="radio"]') && el.tabIndex === -1)
                        return false;
                    return true;
                });
                if (focusables.length === 0)
                    return;
                e.preventDefault();
                var currentIndex = focusables.indexOf(document.activeElement);
                var nextIndex = void 0;
                if (e.shiftKey) {
                    nextIndex = currentIndex <= 0 ? focusables.length - 1 : currentIndex - 1;
                }
                else {
                    nextIndex = currentIndex >= focusables.length - 1 ? 0 : currentIndex + 1;
                }
                focusables[nextIndex].focus();
            }
        };
        // Capture phase to intercept before roving tabindex
        document.addEventListener('keydown', handleKeyDown, true);
        return function () { return document.removeEventListener('keydown', handleKeyDown, true); };
    }, [onClose]);
    return (_jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-label": t('text.shareYourWork'), className: "shareFileWrapper ".concat(displayMode === 'normal' ? "headerTopBeak atHeader" : 'footerBottomBeak atFooter'), style: {
            maxHeight: '553px',
            width: '425px',
            '--beak-height': beakHeight + 'px',
            '--beak-pos': "calc(var(--zoomed-icon-size) / 2)",
        }, children: [_jsx("div", { className: "header", role: "heading", "aria-level": 2, children: _jsx("div", { children: t('text.shareYourWork') }) }), _jsx("img", { className: "shareColorPatch", src: getImageSrc('./assets/images/shareColourPatches.svg'), alt: "", "aria-hidden": "true" }), _jsxs("div", { className: "holder", children: [_jsxs("div", { className: "shareSection bottomLine", role: "group", "aria-label": t('text.shareAnImage'), children: [_jsxs("div", { className: "iconHolder", children: [_jsx("img", { className: "icon", src: getImageSrc('./assets/images/shareAnImage.svg'), alt: "", "aria-hidden": "true" }), _jsx("span", { children: t('text.shareAnImage') })] }), _jsxs("div", { className: "btnContainer", children: [_jsx(ButtonComponent, { clickHandler: function () {
                                            triggerImageAction('downloadSS');
                                        }, text: t('text.save'), ariaLabel: t('text.saveImage'), defaultCSS: "btn", normalCSS: "btnCSS" }), _jsx(ButtonComponent, { clickHandler: function () {
                                            triggerImageAction('copySS');
                                        }, text: t('text.copy'), ariaLabel: t('text.copyImage'), defaultCSS: "btn", normalCSS: "btnCSS" })] })] }), _jsxs("div", { className: "shareSectionMid bottomLine", role: "group", "aria-label": t('text.shareLink'), children: [_jsx("label", { className: "srOnly", htmlFor: "share-link-input", children: t('text.shareLink') }), _jsx("input", { id: "share-link-input", className: "inputBox", value: "https://apps.mathlearningcenter.org/", readOnly: true, "aria-label": t('text.shareLink') }), _jsxs("div", { className: "rowMid", children: [_jsxs("div", { className: "iconHolder", children: [_jsx("img", { className: "icon", src: getImageSrc('./assets/images/linkIcon.svg'), alt: "", "aria-hidden": "true" }), _jsx("span", { children: t('text.shareLink') })] }), _jsx("div", { className: "btnContainer", children: _jsx(ButtonComponent, { text: t('text.copyLink'), ariaLabel: t('text.copyLink'), defaultCSS: "btn", normalCSS: "btnCSS" }) })] })] }), _jsxs("div", { className: "shareSectionBottom", role: "group", "aria-label": t('text.shareACode'), children: [_jsxs("div", { className: "iconHolder", children: [_jsx("img", { className: "sharePopupIcon", src: getImageSrc('./assets/images/popupsmall.svg'), alt: "", "aria-hidden": "true" }), _jsx("span", { children: t('text.shareACode') })] }), _jsxs("div", { className: "btnContainer", children: [_jsx(ButtonComponent, { text: t('text.generateCode'), ariaLabel: t('text.generateCode'), defaultCSS: "btn", normalCSS: "btnCSS" }), _jsx(ButtonComponent, { text: t('text.copyCode'), ariaLabel: t('text.copyCode'), defaultCSS: "btn", normalCSS: "btnCSS" })] })] })] }), imageFeedback && (_jsx(Popup, { isOpen: true, onClose: function () { return setImageFeedback(null); }, ariaLabel: imageFeedback.ok
                    ? ((_b = imageFeedback.info) === null || _b === void 0 ? void 0 : _b.mode) === 'download'
                        ? t('text.imageDownloaded')
                        : t('text.imageCopied')
                    : imageFeedback.reason === 'insecure'
                        ? t('text.copyNeedsSecure')
                        : t('text.copyFailed'), children: _jsxs("div", { className: "iwb-saveInfoModal", role: "dialog", children: [_jsx("p", { className: "iwb-saveInfoMsg", children: imageFeedback.ok
                                ? ((_c = imageFeedback.info) === null || _c === void 0 ? void 0 : _c.mode) === 'download'
                                    ? t('text.imageDownloaded')
                                    : t('text.imageCopied')
                                : imageFeedback.reason === 'insecure'
                                    ? t('text.copyNeedsSecure')
                                    : t('text.copyFailed') }), _jsx(ButtonComponent, { type: "textType1", text: t('text.ok'), defaultCSS: "iwb-saveInfoOkBtn", normalCSS: "iwb-saveInfoOkBtn", clickHandler: function () { return setImageFeedback(null); }, ariaLabel: "text.ok" })] }) }))] }));
};
export default ShareFileModal;
